import type { MatchState, MatchTurned } from '@goodtime/shared';
import type { GameView } from './core';

export type MatchView = GameView<MatchState, MatchTurned>;

/** How a card shows: face down, face up (turned, waiting or just missed),
 *  or found (its pair is found). */
export type CardFace = { up: false } | { up: true; face: string; coins: number; found: boolean };

/**
 * Card `index` as it shows. A miss leaves both cards up until the player's
 * next flip (`missed`), so they can see what they turned. Once the game
 * has ended, every card turned shows (so a loss shows what was missed);
 * cards never turned stay face down.
 */
export function cardAt(view: MatchView, index: number, missed: readonly number[]): CardFace {
  const s = view.state;
  if (!s) return { up: false };
  const seen = [...s.seen].reverse().find((t) => t.index === index);
  if (!seen) return { up: false };
  const found = s.matched.includes(index);
  if (found || s.open === index || missed.includes(index) || view.phase === 'over') {
    return { up: true, face: seen.face, coins: seen.coins, found };
  }
  return { up: false };
}

/** Whether tapping card `index` turns it now. */
export function canFlip(view: MatchView, index: number): boolean {
  const s = view.state;
  return view.phase === 'play' && !(s && (s.matched.includes(index) || s.open === index));
}

/** The two cards of a try that just missed, to show briefly. */
export function missedPair(prev: MatchState | null, next: MatchState, turned: MatchTurned): number[] {
  if (!prev || prev.open === null || next.open !== null) return [];
  return next.matched.includes(turned.index) ? [] : [prev.open, turned.index];
}

/** What the counters mean, said on a tap. The gold pair is named only
 *  when this play can win the pot (the server decides, per play). */
export function matchExplain(tries: number, goldPot: boolean): string {
  const pot = goldPot ? ' The gold pair wins the whole Gold Pot.' : '';
  return `Each try turns two cards, and you have ${tries}. A pair pays the coins on its cards.${pot} Total is what your pairs have paid so far.`;
}

export function matchLead(view: MatchView, goldPot: boolean): string {
  if (view.phase === 'over') {
    const total = view.prize?.coins ?? 0;
    if (view.prize?.gold) return 'The gold pair: the Gold Pot is yours!';
    return total > 0 ? 'Well remembered!' : 'No pairs this time.';
  }
  return goldPot ? 'Find the pairs. The gold pair wins the Gold Pot.' : 'Find the pairs. Each pair pays.';
}
